import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchReference } from '../../lib/lesson-api.js';
import type { FindingClassification } from '../../lib/audit-api.js';
import { CLASSIFICATION_LABEL } from './AuditVocabulary.js';
import { localName, t } from '../../lib/i18n.js';

/**
 * Raising or editing a finding (FN-8). The auditor classifies it and, for a
 * non-conformity, chooses its risk from the organisation's own risk levels —
 * master data, never a value written into the screen (Rule 1). It used to be
 * fixed at "Minor NC, Medium".
 */

export interface FindingDraft {
  classification: FindingClassification;
  riskLevel: string;
  clause: string;
  description: string;
  evidenceSummary: string;
}

const CLASSIFICATIONS: FindingClassification[] = ['conformity', 'observation', 'minor_nc', 'major_nc'];

/** The organisation's risk levels: the stored value, and its name in the reader's language. */
export function useRiskLevels(): { value: string; label: string }[] {
  const reference = useQuery({ queryKey: ['reference', 'audit'], queryFn: () => fetchReference('audit'), staleTime: 10 * 60_000 });
  return (reference.data?.categorisations ?? [])
    .filter((row) => row.category === 'risk_level')
    .map((row) => ({ value: row.label, label: localName(row) }));
}

export function FindingForm({
  initial,
  heading,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
}: {
  initial: FindingDraft;
  heading: string;
  submitLabel: string;
  busy: boolean;
  onSubmit: (draft: FindingDraft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const riskLevels = useRiskLevels();
  const isNc = draft.classification === 'minor_nc' || draft.classification === 'major_nc';
  const ready = draft.description.trim() && (!isNc || draft.riskLevel);

  return (
    <form
      aria-label={heading}
      className="mt-3 grid gap-3 rounded-md border border-border bg-surface p-4 text-sm"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ ...draft, riskLevel: isNc ? draft.riskLevel : '' });
      }}
    >
      <h3 className="m-0 text-base font-semibold">{heading}</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="flex flex-col">
          <span className="mb-1 text-content-secondary">{t("Classification")}</span>
          <select
            className="rounded border border-border-strong px-2 py-1.5"
            value={draft.classification}
            onChange={(event) => setDraft({ ...draft, classification: event.target.value as FindingClassification })}
          >
            {CLASSIFICATIONS.map((value) => (
              <option key={value} value={value}>
                {t(CLASSIFICATION_LABEL[value])}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col">
          <span className="mb-1 text-content-secondary">{t("Risk level")}</span>
          <select
            className="rounded border border-border-strong px-2 py-1.5 disabled:opacity-60"
            value={isNc ? draft.riskLevel : ''}
            disabled={!isNc}
            required={isNc}
            onChange={(event) => setDraft({ ...draft, riskLevel: event.target.value })}
          >
            <option value="">{isNc ? t("Choose a risk level") : t("Only a non-conformity has one")}</option>
            {riskLevels.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col">
          <span className="mb-1 text-content-secondary">{t("Clause (optional)")}</span>
          <input
            className="rounded border border-border-strong px-2 py-1.5"
            value={draft.clause}
            onChange={(event) => setDraft({ ...draft, clause: event.target.value })}
          />
        </label>
      </div>
      <label className="flex flex-col">
        <span className="mb-1 text-content-secondary">{t("What was found")}</span>
        <textarea
          rows={3}
          className="rounded border border-border-strong px-2 py-1.5"
          value={draft.description}
          onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          required
        />
      </label>
      <label className="flex flex-col">
        <span className="mb-1 text-content-secondary">{t("Evidence summary (optional)")}</span>
        <input
          className="rounded border border-border-strong px-2 py-1.5"
          value={draft.evidenceSummary}
          onChange={(event) => setDraft({ ...draft, evidenceSummary: event.target.value })}
        />
      </label>
      <div className="flex gap-2">
        <button type="submit" className="rounded bg-ag-purple px-3 py-1.5 text-white disabled:opacity-60" disabled={busy || !ready}>
          {submitLabel}
        </button>
        <button type="button" className="rounded border border-border px-3 py-1.5" onClick={onCancel}>
          {t("Cancel")}
        </button>
      </div>
    </form>
  );
}

/** A draft as the API takes it: a cleared clause is null, and only a non-conformity carries a risk. */
export function findingBody(draft: FindingDraft): Record<string, unknown> {
  const isNc = draft.classification === 'minor_nc' || draft.classification === 'major_nc';
  return {
    classification: draft.classification,
    description: draft.description.trim(),
    clause: draft.clause.trim() || null,
    evidenceSummary: draft.evidenceSummary.trim(),
    riskLevel: isNc ? draft.riskLevel : null,
  };
}
